import { Box, CircularProgress, styled, Typography } from "@material-ui/core";
import { describeCine } from "../../../_lib/cine/cine-metadata";
import { CineThumbnailBadge } from "../../../_lib/cine/cine-thumbnail-badge";
import { EpocusCinePlayer } from "../../../_lib/cine/epocus-cine-player";
import {
  EPOCUS_ICON_ACTIVE,
  EPOCUS_ON_DARK_MUTED,
  EPOCUS_ON_DARK_PRIMARY,
  EPOCUS_SCAN_BADGE_BG,
  EPOCUS_SCAN_BADGE_BORDER,
  EPOCUS_SCAN_SURFACE,
  EPOCUS_SCAN_THUMB,
  EPOCUS_SCAN_TRAY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import type { HydratedExamImage } from "../../../_lib/hooks/use-epocus-exam-images";

const PaneRoot = styled(Box)({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  backgroundColor: EPOCUS_SCAN_SURFACE,
  minHeight: 260,
});

const ViewsBadge = styled(Typography)({
  position: "absolute",
  top: 14,
  right: 14,
  zIndex: 3,
  maxWidth: "70%",
  padding: "4px 9px",
  borderRadius: 6,
  backgroundColor: EPOCUS_SCAN_BADGE_BG,
  border: `1px solid ${EPOCUS_SCAN_BADGE_BORDER}`,
  color: EPOCUS_ON_DARK_PRIMARY,
  fontSize: rem(11),
});

const Stage = styled(Box)({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  padding: 16,
});

const StillImage = styled("img")({
  maxWidth: "100%",
  maxHeight: 420,
  objectFit: "contain",
  display: "block",
});

const EmptyStage = styled(Typography)({
  color: EPOCUS_ON_DARK_MUTED,
  fontSize: rem(13),
});

const FilmStrip = styled(Box)({
  display: "flex",
  gap: 7,
  padding: "0 16px 14px",
  overflowX: "auto",
  backgroundColor: EPOCUS_SCAN_TRAY,
  "&::-webkit-scrollbar": { display: "none" },
  scrollbarWidth: "none",
});

const Frame = styled("button")({
  flex: "0 0 auto",
  position: "relative",
  width: 72,
  height: 54,
  padding: 0,
  borderRadius: 5,
  overflow: "hidden",
  cursor: "pointer",
  backgroundColor: EPOCUS_SCAN_THUMB,
  border: "1.5px solid transparent",
  "&:focus-visible": {
    outline: `2px solid ${EPOCUS_ICON_ACTIVE}`,
    outlineOffset: 2,
  },
});

const GroupLabel = styled(Typography)({
  flex: "0 0 auto",
  alignSelf: "center",
  color: EPOCUS_ON_DARK_MUTED,
  fontSize: rem(11),
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  padding: "0 2px 0 6px",
});

const FrameImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

export const ReviewScanPane: FC<{
  images: HydratedExamImage[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  viewsLabel: string;
  groupLabelOf?: (image: HydratedExamImage) => string | null;
}> = ({ images, selectedIndex, onSelect, viewsLabel, groupLabelOf }) => {
  const selected = images[selectedIndex];

  return (
    <PaneRoot>
      <ViewsBadge noWrap>{viewsLabel}</ViewsBadge>
      <Stage>
        {!selected ? (
          <EmptyStage>No images on this exam</EmptyStage>
        ) : selected.cine ? (
          <EpocusCinePlayer
            key={selected.id}
            imagingInstanceId={selected.id}
            metadata={selected.cine}
            posterUrl={selected.previewUrl}
            label={selected.viewLabel ?? `Image ${selectedIndex + 1}`}
            active
          />
        ) : selected.loading ? (
          <CircularProgress size={28} style={{ color: EPOCUS_WHITE }} />
        ) : selected.previewUrl ? (
          <StillImage
            src={selected.previewUrl}
            alt={selected.viewLabel ?? `Image ${selectedIndex + 1}`}
          />
        ) : (
          <EmptyStage>Image unavailable offline</EmptyStage>
        )}
      </Stage>
      {images.length > 1 && (
        <FilmStrip>
          {images.map((image, index) => {
            const groupLabel = groupLabelOf?.(image) ?? null;
            const startsGroup =
              groupLabel !== null &&
              (index === 0 || groupLabelOf?.(images[index - 1]) !== groupLabel);
            return [
              startsGroup && (
                <GroupLabel key={`group-${image.id}`}>{groupLabel}</GroupLabel>
              ),
              <Frame
                key={image.id}
                type="button"
                aria-label={[
                  image.viewLabel ?? `Image ${index + 1}`,
                  image.cine && describeCine(image.cine),
                ]
                  .filter(Boolean)
                  .join(", ")}
                aria-pressed={index === selectedIndex}
                onClick={() => onSelect(index)}
                style={{
                  borderColor:
                    index === selectedIndex
                      ? EPOCUS_ICON_ACTIVE
                      : "transparent",
                }}
              >
                {image.previewUrl && (
                  <FrameImage
                    src={image.previewUrl}
                    alt={image.viewLabel ?? `Image ${index + 1}`}
                  />
                )}
                {image.cine && <CineThumbnailBadge metadata={image.cine} />}
              </Frame>,
            ];
          })}
        </FilmStrip>
      )}
    </PaneRoot>
  );
};
